import ImageWithFallback from "~/components/ui/Image/Image";
import React, { useCallback, useMemo } from "react";
import { useLeaderboard } from "~/data/useLeaderboard";
import { classNames, getCurrentWeekNumber } from "~/utils";
import ButtonGroup from "~/components/ui/ButtonGroup/ButtonGroup";
import { useRouter } from "next/router";

const SkeletonItem = () => (
  <tr className="border-b border-gray-700 bg-dark-200 hover:bg-gray-600">
    <td className="w-4 p-4">
      <div className="flex items-center text-base font-semibold">
        <div className="animate-pulse opacity-20">
          <div className="h-6 w-4 bg-dark-100" />
        </div>
      </div>
    </td>
    <th
      scope="row"
      className="flex items-center whitespace-nowrap px-6 py-4 text-white"
    >
      <div className="ps-3">
        <div className="text-base font-semibold">
          <div className="animate-pulse opacity-20">
            <div className={classNames("h-6 w-20 bg-dark-100")} />
          </div>
        </div>
      </div>
    </th>
    <td className="px-6 py-4">
      <div className="animate-pulse opacity-20">
        <div className="h-6 w-20 bg-dark-100" />
      </div>
    </td>
  </tr>
);

const PageNav = ({
  index,
  currentPage,
  onNav,
}: {
  index: number;
  currentPage: number;
  onNav: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <li key={index}>
      <button
        className="flex h-10 items-center justify-center border border-gray-700 bg-dark-200 px-3 text-white hover:bg-blue-100 hover:text-blue-700 aria-selected:bg-dark-100"
        aria-selected={index === currentPage}
        data-value={index}
        onClick={onNav}
      >
        {index + 1}
      </button>
    </li>
  );
};

const PageNavList = ({
  totalPages,
  currentPage,
  onNav,
}: {
  totalPages: number;
  currentPage: number;
  onNav: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  if (totalPages <= 7)
    return (
      <>
        {Array.from({ length: totalPages }).map((_, index) => (
          <PageNav
            key={index}
            index={index}
            currentPage={currentPage}
            onNav={onNav}
          />
        ))}
      </>
    );

  if (currentPage >= 3 && currentPage <= totalPages - 4) {
    return (
      <>
        <PageNav index={0} currentPage={currentPage} onNav={onNav} />
        <li className="bold leading-2 px-2 text-4xl leading-6 text-gray-700">
          ...
        </li>
        <PageNav
          index={currentPage - 1}
          currentPage={currentPage}
          onNav={onNav}
        />
        <PageNav index={currentPage} currentPage={currentPage} onNav={onNav} />
        <PageNav
          index={currentPage + 1}
          currentPage={currentPage}
          onNav={onNav}
        />
        <li className="bold leading-2 px-2 text-4xl leading-6 text-gray-700">
          ...
        </li>
        <PageNav
          index={totalPages - 1}
          currentPage={currentPage}
          onNav={onNav}
        />
      </>
    );
  }

  if (currentPage === totalPages - 3) {
    return (
      <>
        <PageNav index={0} currentPage={currentPage} onNav={onNav} />
        <li className="bold leading-2 px-2 text-4xl leading-6 text-gray-700">
          ...
        </li>
        <PageNav
          index={totalPages - 4}
          currentPage={currentPage}
          onNav={onNav}
        />
        <PageNav
          index={totalPages - 3}
          currentPage={currentPage}
          onNav={onNav}
        />
        <PageNav
          index={totalPages - 2}
          currentPage={currentPage}
          onNav={onNav}
        />
        <PageNav
          index={totalPages - 1}
          currentPage={currentPage}
          onNav={onNav}
        />
      </>
    );
  }

  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <PageNav
          key={index}
          index={index}
          currentPage={currentPage}
          onNav={onNav}
        />
      ))}
      <li className="bold leading-2 px-2 text-4xl leading-6 text-gray-700">
        ...
      </li>
      <PageNav index={totalPages - 2} currentPage={currentPage} onNav={onNav} />
      <PageNav index={totalPages - 1} currentPage={currentPage} onNav={onNav} />
    </>
  );
};

const LeaderboardTable: React.FC<{
  hideHeader?: boolean;
}> = ({ hideHeader }) => {
  const { pathname, query } = useRouter();
  const currentWeekNo = getCurrentWeekNumber();
  const weekNo = (query.week as string) || currentWeekNo.toString();
  const [currentPage, setCurrentPage] = React.useState(0);
  const { data: pages, isLoading } = useLeaderboard();

  const data = useMemo(
    () => pages?.data[currentPage] ?? undefined,
    [pages, currentPage]
  );
  const currentPath = `${pathname}?week=${weekNo}`;

  const HeaderGroups = useMemo(
    () => [
      {
        label: "This week",
        title: "This week",
        href: pathname,
        isExperimental: false,
      },
      {
        label: "Last week",
        title: "Last week",
        href: `${pathname}?week=${currentWeekNo - 1}`,
        isExperimental: false,
      },
      {
        label: "All time",
        title: "All time",
        href: `${pathname}?week=all-time`,
        isExperimental: false,
      },
    ],
    [currentWeekNo, pathname]
  );

  const currentFilterTitle = useMemo(
    () =>
      HeaderGroups.find((item) => item.href === currentPath)?.title ||
      "This week",
    [HeaderGroups, currentPath]
  );

  const totalPages = useMemo(() => pages?.totalPages ?? 1, [pages]);

  const goToPage = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const indexValue = e.currentTarget.dataset.value;
      if (!indexValue) return;
      setCurrentPage(parseInt(indexValue, 10));
    },
    [setCurrentPage]
  );

  return (
    <div className="relative flex-1 space-y-4 overflow-x-auto shadow-md sm:rounded-lg">
      <div className="text-right">
        <ButtonGroup currentTab={currentFilterTitle} groups={HeaderGroups} />
      </div>
      <table className="w-full text-left text-sm text-gray-400 rtl:text-right">
        {!hideHeader && (
          <thead className="bg-primary-100 text-sm uppercase text-white">
            <tr>
              <th scope="col" className="p-4"></th>
              <th scope="col" className="px-6 py-3">
                User
              </th>
              <th scope="col" className="px-6 py-3">
                Plasma points
              </th>
            </tr>
          </thead>
        )}
        <tbody>
          {isLoading &&
            Array.from({ length: 10 }).map((_, index) => (
              <SkeletonItem key={index} />
            ))}
          {data &&
            data?.map((item, index) => (
              <tr
                key={index}
                className="border-b border-gray-700 bg-dark-200 hover:bg-gray-600"
              >
                <td className="w-4 p-4">
                  <div className="flex items-center text-base font-semibold">
                    <div>{item.position}</div>
                  </div>
                </td>
                <th
                  scope="row"
                  className="flex items-center whitespace-nowrap px-6 py-4 text-white"
                >
                  <ImageWithFallback
                    src={item.user?.photoUrl || ""}
                    fallback="/images/rfox.png"
                    width={40}
                    height={40}
                    className="h-10 w-10 rounded-full"
                    alt="avatar"
                  />
                  <div className="ps-3">
                    <div className="text-base font-semibold">
                      {item.displayName || item.user?.name}
                    </div>
                  </div>
                </th>
                <td className="px-6 py-4 text-base font-semibold text-white">
                  {item.value}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <nav
        className="flex-column flex flex-wrap items-center justify-between pt-4 md:flex-row"
        aria-label="Table navigation"
      >
        <span className="mb-4 block w-full text-sm font-normal text-gray-400 md:mb-0 md:inline md:w-auto"></span>
        {!isLoading && totalPages > 1 && (
          <ul className="inline-flex h-10 -space-x-px text-sm rtl:space-x-reverse">
            <li>
              <button
                className="ms-0 flex h-10 items-center justify-center rounded-s-lg border border-gray-700 bg-dark-200 px-3 leading-tight  text-gray-400 hover:bg-gray-700 hover:text-white"
                onClick={() => setCurrentPage((pre) => (pre > 0 ? pre - 1 : 0))}
              >
                Previous
              </button>
            </li>
            <PageNavList
              totalPages={totalPages}
              currentPage={currentPage}
              onNav={goToPage}
            />
            <li>
              <button
                className="flex h-10 items-center justify-center rounded-e-lg border border-gray-700 bg-dark-200 px-3 leading-tight text-gray-400 hover:bg-gray-700 hover:text-white"
                onClick={() =>
                  setCurrentPage((pre) => (pre > totalPages ? pre : pre + 1))
                }
              >
                Next
              </button>
            </li>
          </ul>
        )}
      </nav>
    </div>
  );
};

export default LeaderboardTable;
